import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import {
  ResponsiveContainer, LineChart, Line, XAxis, YAxis, Tooltip, CartesianGrid,
  ReferenceLine, Legend,
} from "recharts";
import { Heart, Brain, Ribbon, Loader2, TrendingDown, TrendingUp, Minus } from "lucide-react";

const META = {
  cardiology: { icon: Heart,  accent: "#F87171", label: "Cardiology" },
  neurology:  { icon: Brain,  accent: "#A78BFA", label: "Neurology" },
  oncology:   { icon: Ribbon, accent: "#FBBF24", label: "Oncology" },
};
const TREND_STYLE = {
  worsening: { color: "#F87171", label: "Worsening", icon: TrendingUp },
  growing:   { color: "#F87171", label: "Growing",   icon: TrendingUp },
  stable:    { color: "#FBBF24", label: "Stable",    icon: Minus },
  improving: { color: "#4ADE80", label: "Improving", icon: TrendingDown },
  shrinking: { color: "#4ADE80", label: "Shrinking", icon: TrendingDown },
  unknown:   { color: "#A3ACA7", label: "Unknown",   icon: Minus },
};

/**
 * SpecialtyTwin — Phase 3c.
 * Cardiology / Neurology / Oncology Digital Twin panel — deterministic
 * projection computed on the backend from real patient facts. Chart is
 * split into actual (solid) vs projected (dashed) via a `kind` field.
 */
export default function SpecialtyTwin({ specialty }) {
  const [twin, setTwin] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const meta = META[specialty] || META.cardiology;
  const Icon = meta.icon;

  useEffect(() => {
    setLoading(true); setErr("");
    (async () => {
      try {
        const { data } = await api.get(`/ai/digital-twin/${specialty}`);
        setTwin(data);
      } catch (e) {
        setErr(e?.response?.data?.detail || "Failed to load twin");
      } finally { setLoading(false); }
    })();
  }, [specialty]);

  if (loading) return (
    <div className="ros-card flex items-center gap-2 text-sm text-[#A3ACA7]"
         data-testid={`twin-loading-${specialty}`}>
      <Loader2 size={14} className="animate-spin" /> Projecting {meta.label} trajectory…
    </div>
  );
  if (err || !twin) return (
    <div className="ros-card text-sm text-[#F87171]" data-testid={`twin-error-${specialty}`}>
      {err || "No projection available."}
    </div>
  );

  const trend = TREND_STYLE[twin.trend] || TREND_STYLE.unknown;
  const TrendIcon = trend.icon;
  const yKey = specialty === "cardiology" ? "risk_pct"
             : specialty === "neurology"  ? "count"
             : "sld";
  const chartData = (twin.trajectory || []).map((p) => ({
    date: p.date,
    actual: (!p.kind || p.kind === "actual") ? p[yKey] : null,
    projected: p.kind === "projected" ? p[yKey] : null,
  }));
  // Bridge the last actual → first projected so the dashed line connects
  const lastActualIdx = chartData.map((p) => p.actual != null).lastIndexOf(true);
  if (lastActualIdx !== -1 && chartData[lastActualIdx + 1]?.projected != null) {
    chartData[lastActualIdx + 1].actual = chartData[lastActualIdx].actual;
  }

  return (
    <div className="space-y-4" data-testid={`twin-panel-${specialty}`}>
      <div className="ros-card" style={{ borderLeftWidth: 2, borderLeftColor: meta.accent }}>
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div className="flex items-start gap-3">
            <div className="w-11 h-11 rounded-2xl flex items-center justify-center flex-shrink-0"
                 style={{ background: `${meta.accent}22`, border: `1px solid ${meta.accent}55` }}>
              <Icon size={20} style={{ color: meta.accent }} />
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-wider text-[#7A8480]">{meta.label} Digital Twin</div>
              <div className="font-heading text-xl text-white mt-0.5">{twin.metric}</div>
            </div>
          </div>
          <div className="text-right">
            <div className="flex items-center gap-1.5 justify-end">
              <TrendIcon size={14} style={{ color: trend.color }} />
              <span className="text-[11px] font-bold uppercase tracking-wider" style={{ color: trend.color }}>
                {trend.label}
              </span>
            </div>
            <div className="mt-1 flex items-baseline gap-2 justify-end">
              <span className="text-[10px] text-[#7A8480]">baseline</span>
              <span className="font-mono text-white">{twin.baseline ?? "—"}</span>
              <span className="text-[10px] text-[#7A8480]">→ final</span>
              <span className="font-mono text-white">{twin.final ?? "—"}</span>
              <span className="text-[10px] text-[#7A8480]">{twin.unit}</span>
            </div>
          </div>
        </div>

        {chartData.length > 0 && (
          <div className="h-64 mt-4" data-testid={`twin-chart-${specialty}`}>
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={chartData} margin={{ top: 10, right: 16, left: -12, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#272E2B" />
                <XAxis dataKey="date" stroke="#A3ACA7" fontSize={10}
                       tickFormatter={(v) => v.length > 7 ? v.slice(5) : v} />
                <YAxis stroke="#A3ACA7" fontSize={11} />
                <Tooltip contentStyle={{ background: "#0F1312", border: "1px solid #272E2B", borderRadius: 12 }} />
                <Legend wrapperStyle={{ fontSize: 11 }} />
                <Line type="monotone" dataKey="actual"    name="Actual" stroke={meta.accent} strokeWidth={2.5} dot={{ r: 3 }} />
                <Line type="monotone" dataKey="projected" name="Projected" stroke={meta.accent}
                      strokeWidth={2} strokeDasharray="6 6" dot={{ r: 3 }} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        )}

        <div className="text-sm text-[#D1D5DB] mt-4 leading-relaxed" data-testid={`twin-narrative-${specialty}`}>
          {twin.narrative}
        </div>
      </div>

      {twin.levers?.length > 0 && (
        <div className="ros-card" data-testid={`twin-levers-${specialty}`}>
          <div className="ros-label mb-3">Modifiable levers</div>
          <div className="space-y-2">
            {twin.levers.map((l, i) => (
              <div key={i} className="flex items-center justify-between gap-3 py-2 border-b border-[#272E2B] last:border-0">
                <div className="flex-1 min-w-0">
                  <div className="text-sm text-white">{l.factor}</div>
                  <div className="text-[11px] text-[#A3ACA7]">
                    current: {l.current} · target: {l.target}
                  </div>
                </div>
                <div className="text-right">
                  <span className="text-[11px] font-bold px-2 py-0.5 rounded-full"
                        style={{ color: "#4ADE80", background: "rgba(74,222,128,0.12)",
                                 border: "1px solid rgba(74,222,128,0.35)" }}>
                    {l.impact_pct > 0 ? "+" : ""}{l.impact_pct}% risk
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
